<script setup lang="ts">
/**
 * Inline find (and replace) bar for a document, a sheet or a code view:
 * query input with the „3 von 12“ count, previous / next, match case and
 * whole word toggles, and — when `replaceable` — a disclosure to the replace
 * row with „Ersetzen“ and „Alle ersetzen“.
 *
 * Presentational only: the caller searches. The bar reports the query and the
 * options through `v-model`s and asks for `next`, `prev`, `replace`,
 * `replaceAll` and `close`; it shows `current` (0-based, `null` for none) of
 * `total` matches. Enter goes to the next match, Shift+Enter to the previous
 * one, Escape closes. The query input takes the focus when the bar appears.
 * Enter and Escape belong to an input method while it composes (and to the
 * moment right after: Safari sends the Enter that confirmed the composition
 * after compositionend), so they neither step nor close then.
 *
 * `mode="navigate"` is the bar without the search: the caller already has the
 * matches (a hit list jumped to the place, an editor marks the terms of a
 * query) and the bar only steps through them. There is no query input, no
 * replace row and no match-case / whole-word toggle (`query`, `replaceable`,
 * `options` and the models have no effect). The `label` slot sits where the
 * field was — the query sentence („Gesucht: …“); it is no live region, the
 * count announces. A long sentence wraps to two lines before it ends in an
 * ellipsis, and a word longer than the line breaks. The count („2 von 5“)
 * shows without a query string: for no match it reads `noMatchLabel`, or
 * „0 von 0“ with `showZeroCount`.
 * Previous, next and close stay, and `findLabel` still names the bar.
 * `autofocus` puts the focus on „Nächster Treffer“ (a disabled button cannot
 * take it, so not while `total` is 0).
 * Enter steps on, Shift+Enter steps back and Escape closes from anywhere in
 * the bar, with the same input-method guard; only a focused control keeps its
 * own Enter — „Vorheriger Treffer“, „Suche schließen“, a link in the label.
 * Without a query input the exposed `focus()` lands on „Nächster Treffer“
 * and `focusReplace()` does nothing.
 *
 * The optional `leading` slot is navigate mode only: the first item of the row,
 * in front of the label (an info button that explains the bar). A control in it
 * keeps its own Enter. Find mode renders none, so its replace row stays aligned
 * under the query input. Without the slot nothing is rendered for it.
 *
 * Escape emits `escape` when the caller listens to it (`@escape`), else `close`
 * as before: a caller that wants Escape to do something other than ✕ binds it.
 */
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js'; import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js'; import ChevronUp from 'lucide-vue-next/dist/esm/icons/chevron-up.js'; import X from 'lucide-vue-next/dist/esm/icons/x.js';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  useId,
  useTemplateRef,
} from 'vue';
import { NxButton } from '../Button';
import { unrefElement } from '../Composables/element';
import { NxIconButton } from '../IconButton';

const props = withDefaults(
  defineProps<{
    /**
     * `find` (default) is the search bar. `navigate` steps through matches the
     * caller already has: no query input, replace row or option toggles, the
     * `label` slot in place of the field.
     */
    mode?: 'find' | 'navigate';
    /** 0-based index of the current match, `null` when none is selected. */
    current?: number | null;
    /** Number of matches for the query. */
    total?: number;
    /** Offer the replace row. */
    replaceable?: boolean;
    /** Show the match-case and whole-word toggles. */
    options?: boolean;
    findLabel?: string;
    replaceLabel?: string;
    replaceAllLabel?: string;
    previousLabel?: string;
    nextLabel?: string;
    closeLabel?: string;
    matchCaseLabel?: string;
    wholeWordLabel?: string;
    toggleReplaceLabel?: string;
    /** Count text; `{current}` and `{total}` are replaced. */
    countLabel?: string;
    noMatchLabel?: string;
    /**
     * Show the count as `0/0` (`countLabel` with zeros) when nothing matches —
     * also before anything is typed — instead of `noMatchLabel`: an editor
     * whose find bar always reads „current/total“.
     */
    showZeroCount?: boolean;
    /** Take the focus when mounted. */
    autofocus?: boolean;
  }>(),
  {
    mode: 'find',
    current: null,
    total: 0,
    replaceable: false,
    options: true,
    findLabel: 'Suchen',
    replaceLabel: 'Ersetzen',
    replaceAllLabel: 'Alle ersetzen',
    previousLabel: 'Vorheriger Treffer',
    nextLabel: 'Nächster Treffer',
    closeLabel: 'Suche schließen',
    matchCaseLabel: 'Groß-/Kleinschreibung beachten',
    wholeWordLabel: 'Nur ganze Wörter',
    toggleReplaceLabel: 'Ersetzen ein-/ausblenden',
    countLabel: '{current} von {total}',
    noMatchLabel: 'Keine Treffer',
    showZeroCount: false,
    autofocus: true,
  }
);

const query = defineModel<string>('query', { default: '' });
const replacement = defineModel<string>('replacement', { default: '' });
const matchCase = defineModel<boolean>('matchCase', { default: false });
const wholeWord = defineModel<boolean>('wholeWord', { default: false });
/** Whether the replace row is shown. */
const replaceOpen = defineModel<boolean>('replaceOpen', { default: false });

const emit = defineEmits<{
  next: [];
  prev: [];
  replace: [];
  replaceAll: [];
  close: [];
  /** Escape. Without a listener Escape emits `close`, as it always did. */
  escape: [];
}>();
const instance = getCurrentInstance();

defineSlots<{
  /**
   * Navigate mode only: the first item of the row, before the label (an info or
   * help button that explains the bar). A control in it keeps its own Enter,
   * like a link in the label. Without it the markup is unchanged.
   */
  leading?(): unknown;
  /**
   * Navigate mode: sits where the query input was — the query sentence
   * („Gesucht: EPDM oder TPE“). No live region; the count announces.
   */
  label?(): unknown;
}>();

const id = useId();
const findRef = useTemplateRef<HTMLInputElement>('find');
const replaceRef = useTemplateRef<HTMLInputElement>('replaceField');
const nextRef = useTemplateRef<InstanceType<typeof NxIconButton>>('next');

const navigate = computed(() => props.mode === 'navigate');
const hasMatches = computed(() => props.total > 0);
const countText = computed(() => {
  // Navigate mode has no query string: the label tells what is searched, the count always shows.
  if (!navigate.value && !query.value && !props.showZeroCount) return '';
  if (!hasMatches.value && !props.showZeroCount) return props.noMatchLabel;
  const current = !hasMatches.value || props.current === null ? 0 : props.current + 1;
  return props.countLabel
    .replace('{current}', String(current))
    .replace('{total}', String(props.total));
});

// An input method owns Enter/Escape while it composes, and for a moment after
// compositionend (Safari delivers the confirming Enter after it).
const JUST_COMPOSED_MS = 80;
let justComposed = false;
let justComposedTimer: ReturnType<typeof setTimeout> | undefined;
function onCompositionEnd() {
  justComposed = true;
  if (justComposedTimer !== undefined) clearTimeout(justComposedTimer);
  justComposedTimer = setTimeout(() => {
    justComposed = false;
    justComposedTimer = undefined;
  }, JUST_COMPOSED_MS);
}
onBeforeUnmount(() => {
  if (justComposedTimer !== undefined) clearTimeout(justComposedTimer);
});
function composing(event: KeyboardEvent): boolean {
  return justComposed || event.isComposing || event.keyCode === 229;
}

function onFindKeydown(event: KeyboardEvent) {
  if (composing(event)) return;
  if (event.key === 'Enter') {
    event.preventDefault();
    if (!hasMatches.value) return;
    if (event.shiftKey) emit('prev');
    else emit('next');
  }
}
function onReplaceKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter' || composing(event)) return;
  event.preventDefault();
  if (!hasMatches.value) return;
  if (event.metaKey || event.ctrlKey) emit('replaceAll');
  else emit('replace');
}
// Navigate mode has no query input to take Enter, so the bar does. What keeps its own Enter is a
// control inside the bar: Enter on a focused „Vorheriger Treffer“ or „Suche schließen“ clicks it,
// and the bar must not turn that into another step. „Nächster Treffer“ is where the focus starts —
// it steps either way, so that Shift+Enter there can step back.
const OWN_ENTER =
  'a[href], button, input, select, textarea, summary, [contenteditable], [role="button"]';
function keepsEnter(event: KeyboardEvent): boolean {
  const bar = event.currentTarget;
  const target = event.target;
  if (!(bar instanceof Element) || !(target instanceof Element)) return false;
  const control = target.closest(OWN_ENTER);
  if (!control || !bar.contains(control)) return false;
  return !unrefElement(nextRef)?.contains(control);
}
function onKeydown(event: KeyboardEvent) {
  if (composing(event)) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    // A bound `@escape` takes the key; otherwise it is the same as ✕.
    if (instance?.vnode.props?.onEscape) emit('escape');
    else emit('close');
    return;
  }
  if (!navigate.value || event.key !== 'Enter' || keepsEnter(event)) return;
  event.preventDefault();
  if (!hasMatches.value) return;
  if (event.shiftKey) emit('prev');
  else emit('next');
}

function focus() {
  if (navigate.value) {
    // „Nächster Treffer“: Enter steps on from there. Disabled without matches, it keeps no focus.
    const next = unrefElement(nextRef);
    if (next instanceof HTMLElement) next.focus();
    return;
  }
  findRef.value?.focus();
  findRef.value?.select();
}

onMounted(() => {
  if (props.autofocus) focus();
});

defineExpose({
  /** Focuses the query input and selects its text; navigate mode focuses „Nächster Treffer“. */
  focus,
  /** Focuses the replace field (opening the row); navigate mode has none and does nothing. */
  focusReplace: () => {
    if (navigate.value) return;
    replaceOpen.value = true;
    requestAnimationFrame(() => replaceRef.value?.focus());
  },
});
</script>

<template>
  <div
    class="nx-find-bar"
    role="search"
    :aria-label="props.findLabel"
    data-testid="nx-find-bar"
    @keydown="onKeydown"
  >
    <div class="nx-find-bar__row">
      <slot v-if="navigate" name="leading" />
      <NxIconButton
        v-if="props.replaceable && !navigate"
        height="xs"
        :title="props.toggleReplaceLabel"
        :aria-label="props.toggleReplaceLabel"
        :aria-expanded="replaceOpen"
        :aria-controls="`${id}-replace`"
        data-testid="nx-find-toggle-replace"
        @click="replaceOpen = !replaceOpen"
      >
        <ChevronDown v-if="replaceOpen" class="nxt-icon-sm" aria-hidden="true" />
        <ChevronRight v-else class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <div
        v-if="!navigate"
        class="nx-find-bar__field"
        :class="{ 'is-empty': !!query && !hasMatches }"
      >
        <input
          ref="find"
          v-model="query"
          class="nx-find-bar__input"
          type="search"
          :placeholder="props.findLabel"
          :aria-label="props.findLabel"
          :aria-describedby="`${id}-count`"
          data-testid="nx-find-query"
          @keydown="onFindKeydown"
          @compositionend="onCompositionEnd"
        />
        <span
          :id="`${id}-count`"
          class="nx-find-bar__count"
          aria-live="polite"
          data-testid="nx-find-count"
        >
          {{ countText }}
        </span>
        <template v-if="props.options">
          <button
            type="button"
            class="nx-find-bar__option"
            :aria-pressed="matchCase"
            :title="props.matchCaseLabel"
            :aria-label="props.matchCaseLabel"
            data-testid="nx-find-match-case"
            @click="matchCase = !matchCase"
          >
            Aa
          </button>
          <button
            type="button"
            class="nx-find-bar__option is-word"
            :aria-pressed="wholeWord"
            :title="props.wholeWordLabel"
            :aria-label="props.wholeWordLabel"
            data-testid="nx-find-whole-word"
            @click="wholeWord = !wholeWord"
          >
            ab
          </button>
        </template>
      </div>
      <template v-else>
        <span class="nx-find-bar__label" data-testid="nx-find-label"><slot name="label" /></span>
        <span
          :id="`${id}-count`"
          class="nx-find-bar__count"
          :class="{ 'is-empty': !hasMatches }"
          aria-live="polite"
          data-testid="nx-find-count"
        >
          {{ countText }}
        </span>
      </template>
      <NxIconButton
        height="xs"
        :title="props.previousLabel"
        :aria-label="props.previousLabel"
        :disabled="!hasMatches"
        data-testid="nx-find-prev"
        @click="emit('prev')"
      >
        <ChevronUp class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <NxIconButton
        ref="next"
        height="xs"
        :title="props.nextLabel"
        :aria-label="props.nextLabel"
        :disabled="!hasMatches"
        data-testid="nx-find-next"
        @click="emit('next')"
      >
        <ChevronDown class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
      <NxIconButton
        height="xs"
        :title="props.closeLabel"
        :aria-label="props.closeLabel"
        data-testid="nx-find-close"
        @click="emit('close')"
      >
        <X class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
    </div>
    <div
      v-if="props.replaceable && replaceOpen && !navigate"
      :id="`${id}-replace`"
      class="nx-find-bar__row is-replace"
    >
      <div class="nx-find-bar__field">
        <input
          ref="replaceField"
          v-model="replacement"
          class="nx-find-bar__input"
          type="text"
          :placeholder="props.replaceLabel"
          :aria-label="props.replaceLabel"
          data-testid="nx-find-replacement"
          @keydown="onReplaceKeydown"
          @compositionend="onCompositionEnd"
        />
      </div>
      <NxButton
        height="xs"
        variant="outline"
        :disabled="!hasMatches"
        data-testid="nx-find-replace"
        @click="emit('replace')"
      >
        {{ props.replaceLabel }}
      </NxButton>
      <NxButton
        height="xs"
        variant="outline"
        :disabled="!hasMatches"
        data-testid="nx-find-replace-all"
        @click="emit('replaceAll')"
      >
        {{ props.replaceAllLabel }}
      </NxButton>
    </div>
  </div>
</template>

<style scoped>
.nx-find-bar {
  display: inline-flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  max-width: 100%;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-md);
  animation: nx-find-in var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-find-bar__row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
/* The replace row lines its field up under the query input. */
.nx-find-bar__row.is-replace {
  padding-inline-start: calc(var(--nxt-control-size-xs) + var(--nxt-space-2xs));
}
.nx-find-bar__field {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: calc(var(--nxt-size-unit) * 12);
  height: var(--nxt-control-size-xs);
  padding-inline: var(--nxt-space-xs) var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-bg);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-find-bar__field:focus-within {
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-find-bar__field.is-empty {
  border-color: var(--nxt-danger-border);
}
.nx-find-bar__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  outline: none;
}
.nx-find-bar__input::-webkit-search-cancel-button {
  display: none;
}
.nx-find-bar__count {
  flex: 0 0 auto;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nx-find-bar__field.is-empty .nx-find-bar__count,
.nx-find-bar__count.is-empty {
  color: var(--nxt-danger-subtle-fg);
}
/* Navigate mode: no field chrome — the query sentence sits on the bar itself. It wraps to two lines and
   only then gives way (ellipsis at the end of the second): a long sentence stays readable, and
   it cannot push the count and the steps off the bar. */
.nx-find-bar__label {
  flex: 1 1 auto;
  min-width: 0;
  padding-inline: var(--nxt-space-xs);
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  overflow-wrap: anywhere;
}
.nx-find-bar__option {
  flex: 0 0 auto;
  min-width: calc(var(--nxt-control-size-xs) * 0.8);
  height: calc(var(--nxt-control-size-xs) * 0.75);
  padding: 0 var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  cursor: pointer;
}
.nx-find-bar__option.is-word {
  text-decoration: underline;
  text-underline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-find-bar__option:hover {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}
.nx-find-bar__option[aria-pressed='true'] {
  border-color: var(--nxt-brand-border);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-find-bar__option:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
@keyframes nx-find-in {
  from {
    opacity: 0;
    transform: translateY(calc(var(--nxt-hairline) * -4));
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-find-bar,
  .nx-find-bar__field {
    animation: none;
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-find-bar__option[aria-pressed='true'] {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
